import { overflowWithin } from "@mhmo91/schmancy/directives";
import type { SchmancyInput } from "@mhmo91/schmancy/form";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import {
	confirm,
	type OverlayBeforeCloseDetail,
} from "@mhmo91/schmancy/overlay";
import { html, nothing, type PropertyValues, type TemplateResult } from "lit";
import { property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { filter, fromEvent, merge, takeUntil, tap } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import { organizationId } from "../../workspace/organization.state.ts";
import { workspaceDomain } from "../../workspace/workspace.state.ts";
import {
	INTEGRATION_TONE_CLASS,
	type IntegrationStatusLine,
	NOT_CONFIGURED,
} from "../status.state.ts";
import type { RevolutEnvId } from "./wizard.state.ts";
import { revolutSaveCanceled$, revolutSaveIntent$ } from "./wizard.state.ts";

// developer.revolut.com's Merchant getting-started page — covers the account
// model, applying for a Merchant account, and generating API keys.
const REVOLUT_DOCS_URL =
	"https://developer.revolut.com/docs/guides/merchant/get-started";

// The exact message integrations.const.ts's own headerSafe regex would
// itself produce server-side for the same input.
const NO_CONTROL_CHARS = /^[\x21-\x7E]+$/;

// ISO-4217: exactly three letters, case-insensitive as typed — normalized
// to uppercase only at the point the save intent is dispatched.
const CURRENCY_FORMAT = /^[A-Za-z]{3}$/;

/**
 * The bare host Revolut's Apple Pay domain-registration endpoint takes: it
 * appends /.well-known/… to whatever it is given, so a scheme, a path, or a
 * trailing slash makes an unfetchable URL and the call 400s. Both this
 * field's sources can carry one — the seam declares `domain` as a filled
 * string with no shape constraint, and a stored value predating the server's
 * own normalization keeps whatever scheme it was saved with — so the field
 * opens on the corrected host the operator can confirm rather than
 * re-submitting a value that 400s.
 */
export function bareHost(domain: string): string {
	return domain
		.trim()
		.replace(/^(?:https?:)?\/\//i, "")
		.replace(/[/?#].*$/, "")
		.toLowerCase();
}

@schmancyElement("revolut-wizard")
export class RevolutWizardView extends LocalizeMixin(SchmancyElement) {
	/** The organization this wizard opened under, read once at construction. */
	private readonly orgId = organizationId.value;

	@property({ attribute: false }) envId!: RevolutEnvId;

	@property({ attribute: false }) status: IntegrationStatusLine =
		NOT_CONFIGURED;

	@property({ type: Boolean }) saving = false;
	@property({ type: Boolean }) active = false;
	@property({ type: Boolean }) requiresConfirmationCode = false;
	@state() private replacingSecret = false;
	@state() private replacingPublic = false;
	private initialCurrency = "";
	private initialDomain: string | null = null;
	@property({ attribute: false }) lastSaveOk: boolean | null = null;

	/** True only once a key has previously been proven and stored for THIS
	 *  environment — what makes a blank secret-key field mean "keep the
	 *  stored one", and what gates the overwrite confirm when one is typed. */
	@property({ type: Boolean }) hasStoredCredential = false;

	/** True once THIS environment already holds a publishable key on the
	 *  settings doc — the publishable key's own sibling of
	 *  `hasStoredCredential`, and what makes a blank publishable-key field
	 *  mean "keep the stored one" while activating. The server resolves an
	 *  omitted publicKey against the stored one and only refuses the
	 *  activation when neither exists, so the field is genuinely required
	 *  only where nothing is stored. `hasStoredCredential` cannot carry this:
	 *  it tracks the SECRET key, and the two are stored independently. */
	@property({ type: Boolean }) hasStoredPublicKey = false;

	/** The Apple Pay domain this workspace already has registered, or null
	 *  when none is stored — the field's seed, so an untouched save re-submits
	 *  the host that is already in effect instead of replacing a deliberately
	 *  different one with this deployment's own domain. */
	@property({ attribute: false }) storedApplePayDomain: string | null = null;

	/** Dies with the overlay — every field here is this save's own one-shot
	 *  input, never a shared draft singleton the way the old multi-field
	 *  wizard needed one. */
	@state() private secretKey = "";
	@state() private publicKey = "";
	@state() private currency = "";
	/** null until the operator types: the field renders its seed — the stored
	 *  domain, else this deployment's own — as a prefilled `.value` rather than
	 *  a placeholder, since a placeholder submits nothing unless typed into and
	 *  the operator's job here is to confirm the host, not compose it. */
	@state() private applePayDomain: string | null = null;
	@state() private activate = false;
	/** Whether a save has been attempted from THIS mount — the footer's "done"
	 *  state belongs to a save the operator just made, not to a prior one
	 *  whose verified status was already on the row when the overlay opened.
	 *  Same scoping lettermint.state.ts's lettermintAttempted carries for its
	 *  own form, kept local here since this wizard's fields already are. */
	@state() private attempted = false;
	@state() private admitted = false;
	@state() private confirming = false;

	private readonly secretKeyRef = createRef<SchmancyInput & HTMLElement>();
	private readonly publicKeyRef = createRef<SchmancyInput & HTMLElement>();
	private readonly currencyRef = createRef<SchmancyInput & HTMLElement>();
	private readonly applePayDomainRef = createRef<SchmancyInput & HTMLElement>();

	/** Each field's own regex-derived message, shared by render() (hints) and
	 *  updated() (setCustomValidity — see updated() for why a plain ?error=
	 *  binding can't carry this under validateOn:'dirty'). */
	private validationErrors(activating = false): {
		secretKey: string | null;
		publicKey: string | null;
		currency: string | null;
		applePayDomain: string | null;
	} {
		const trimmed = this.secretKey.trim();
		const hasBadChars = trimmed.length > 0 && !NO_CONTROL_CHARS.test(trimmed);
		const missingPrefix =
			trimmed.length > 0 && !hasBadChars && !trimmed.startsWith("sk_");
		const secretKey = hasBadChars
			? this.t("admin.integrations.revolut.wizard.secretKeyBadCharsError")
			: missingPrefix
				? this.t(
						"admin.integrations.revolut.wizard.secretKeyMissingPrefixError",
					)
				: null;

		// Only where NOTHING is stored: with a publishable key already on this
		// environment, a blank field is the same "keep the stored one" the
		// server itself resolves — activating with none anywhere is still the
		// real error this message was written for.
		const publicKey =
			activating &&
			!this.hasStoredPublicKey &&
			this.publicKey.trim().length === 0
				? this.t(
						"admin.integrations.revolut.wizard.publicKeyRequiredWhenActivateError",
					)
				: null;

		const currencyTrimmed = this.currency.trim();
		const currency =
			currencyTrimmed.length > 0 && !CURRENCY_FORMAT.test(currencyTrimmed)
				? this.t("admin.integrations.revolut.wizard.currencyFormatError")
				: null;

		const applePayDomainTrimmed = (
			this.applePayDomain ??
			bareHost(this.storedApplePayDomain ?? workspaceDomain.get())
		).trim();
		const applePayDomain =
			this.envId === "revolutLive" &&
			applePayDomainTrimmed.length > 0 &&
			!NO_CONTROL_CHARS.test(applePayDomainTrimmed)
				? this.t("admin.integrations.revolut.wizard.applePayDomainFormatError")
				: null;

		return { secretKey, publicKey, currency, applePayDomain };
	}

	/** schmancy-input's own checkValidity() (native + custom validity only)
	 *  reruns after every `.value` change and would silently overwrite a plain
	 *  `?error=` binding the instant validateOn:'dirty' opens its show-error
	 *  gate (formField.mixin.ts's checkValidity() only knows required/custom
	 *  validity, never an externally-bound prop). Routing each field's own
	 *  regex-derived message through setCustomValidity() folds it into that
	 *  same validity engine instead of racing it. */
	protected override updated(changedProps: PropertyValues): void {
		super.updated(changedProps);

		const errors = this.validationErrors();
		this.secretKeyRef.value?.setCustomValidity(errors.secretKey ?? "");
		this.publicKeyRef.value?.setCustomValidity(errors.publicKey ?? "");
		this.currencyRef.value?.setCustomValidity(errors.currency ?? "");
		this.applePayDomainRef.value?.setCustomValidity(
			errors.applePayDomain ?? "",
		);
	}

	override willUpdate(changed: PropertyValues<this>) {
		super.willUpdate(changed);
		if (changed.has("saving") && !this.saving) this.admitted = false;
		if (
			this.attempted &&
			!this.saving &&
			this.lastSaveOk === true &&
			(changed.has("saving") || changed.has("lastSaveOk"))
		) {
			this.secretKey = "";
			this.publicKey = "";
			this.replacingSecret = false;
			this.replacingPublic = false;
			this.initialCurrency = this.currency;
			this.initialDomain = this.applePayDomain;
		}
	}

	override connectedCallback(): void {
		super.connectedCallback();
		const root = this.getRootNode();
		if (!(root instanceof ShadowRoot)) return;
		merge(
			fromEvent(this, "revolut-switch-request").pipe(
				tap((event) => {
					event.stopPropagation();
					void this.close(true);
				}),
			),
			revolutSaveCanceled$.pipe(
				filter(
					(result) =>
						result.orgId === this.orgId && result.envId === this.envId,
				),
				tap(() => {
					this.admitted = false;
					this.confirming = false;
				}),
			),
			fromEvent<CustomEvent<OverlayBeforeCloseDetail>>(
				root.host,
				"beforeclose",
			).pipe(
				tap((event) => {
					if (
						!this.dirty() &&
						!this.saving &&
						!this.admitted &&
						!this.confirming
					)
						return;
					event.preventDefault();
					if (event.detail.reason === "structured") void this.close();
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	}
	private dirty(): boolean {
		return (
			this.secretKey !== "" ||
			this.publicKey !== "" ||
			this.currency !== this.initialCurrency ||
			this.applePayDomain !== this.initialDomain
		);
	}
	private async close(navigate = false): Promise<void> {
		if (this.saving || this.admitted || this.confirming) return;
		if (this.dirty()) {
			this.confirming = true;
			const discard = await confirm({
				title: this.t("admin.integrations.metaAds.form.wizard.discardTitle"),
				confirmText: this.t(
					"admin.integrations.metaAds.form.wizard.discardButton",
				),
				cancelText: this.t(
					"admin.integrations.metaAds.form.wizard.keepEditing",
				),
				variant: "danger",
			});
			this.confirming = false;
			if (!discard || !this.isConnected) return;
		}
		if (navigate)
			this.dispatchEvent(
				new CustomEvent("revolut-switch", { bubbles: true, composed: true }),
			);
		this.dispatchEvent(
			new CustomEvent("close", { bubbles: true, composed: true }),
		);
	}

	private async save(secretKey: string, envLabel: string): Promise<void> {
		if (this.saving || this.confirming || this.admitted) return;
		this.confirming = true;
		if (this.activate && !this.requiresConfirmationCode) {
			const ok = await confirm({
				title: this.t(
					"admin.integrations.revolut.wizard.activateConfirmTitle",
					{
						env: envLabel,
					},
				),
				message: this.t(
					"admin.integrations.revolut.wizard.activateConfirmMessage",
					{ env: envLabel },
				),
				confirmText: this.t(
					"admin.integrations.revolut.wizard.activateConfirmButton",
				),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!ok) {
				this.confirming = false;
				return;
			}
		} else if (this.hasStoredCredential && secretKey.length > 0) {
			const ok = await confirm({
				title: this.t(
					"admin.integrations.revolut.wizard.overwriteConfirmTitle",
				),
				message: this.t(
					"admin.integrations.revolut.wizard.overwriteConfirmMessage",
				),
				confirmText: this.t(
					"admin.integrations.revolut.wizard.overwriteConfirmButton",
				),
				cancelText: this.t("admin.common.cancel"),
				variant: "danger",
			});
			if (!ok) {
				this.confirming = false;
				return;
			}
		}
		const publicKey = this.publicKey.trim();
		const currency = this.currency.trim().toUpperCase();
		const applePayDomain = (
			this.applePayDomain ??
			bareHost(this.storedApplePayDomain ?? workspaceDomain.get())
		).trim();
		// Every field is a conditional KEY, never an explicit undefined: the
		// receiving contract reads an absent secret field as "keep what is
		// stored" and rejects an empty string outright (.min(1)).
		this.admitted = true;
		revolutSaveIntent$.next({
			orgId: this.orgId,
			envId: this.envId,
			...(secretKey.length > 0 && { secretKey }),
			...(this.activate && { activate: true }),
			...(publicKey.length > 0 && { publicKey }),
			...(currency.length > 0 && { currency }),
			// Live-only, and the field only renders there — the prefilled default
			// would otherwise ride along on a sandbox save that never showed it.
			...(this.envId === "revolutLive" &&
				applePayDomain.length > 0 && { applePayDomain }),
		});
		this.attempted = true;
		this.confirming = false;
	}

	override render(): TemplateResult {
		const envLabel = this.t(
			this.envId === "revolutLive"
				? "admin.integrations.revolut.wizard.envLive"
				: "admin.integrations.revolut.wizard.envSandbox",
		);
		const errors = this.validationErrors();
		const canSubmit =
			(this.secretKey.trim().length > 0 || this.hasStoredCredential) &&
			Object.values(errors).every((error) => error === null);
		const busy = this.saving || this.admitted || this.confirming;
		const verifySandbox =
			this.envId === "revolutSandbox" && this.requiresConfirmationCode;
		return html`<div class="flex max-h-[85dvh] w-[min(28rem,92vw)] max-w-full flex-col">
   <header class="flex items-center justify-between gap-4 px-5 pt-4 pb-3"><schmancy-typography type="headline" token="md">${envLabel}</schmancy-typography>${!verifySandbox ? html`<schmancy-button data-overlay-close variant="text" ?disabled=${busy} aria-label=${this.t("admin.integrations.revolut.wizard.closeAriaLabel", { env: envLabel })} @click=${() => this.close()}>${this.t("admin.common.close")}</schmancy-button>` : nothing}</header>
   <main ?inert=${busy} ${overflowWithin()} class="flex min-h-0 flex-1 flex-col gap-4 px-5 pb-5">
    ${this.status.tone === "error" || this.status.tone === "warning" ? html`<schmancy-typography role="alert" type="body" token="sm" class=${INTEGRATION_TONE_CLASS[this.status.tone]}>${this.t(this.status.message, this.status.params)}</schmancy-typography>` : nothing}
    ${
			this.hasStoredCredential && !this.replacingSecret
				? html`<schmancy-surface type="containerLow" class="rounded-xl p-3 flex items-center justify-between gap-3"><schmancy-typography type="body" token="md">${this.t("admin.integrations.revolut.wizard.secretKeyLabel", { env: envLabel })}</schmancy-typography><schmancy-button variant="text" @click=${() => {
						this.replacingSecret = true;
					}}>${this.t("admin.integrations.metaAds.form.wizard.replace")}</schmancy-button></schmancy-surface>`
				: html`<schmancy-input ${ref(this.secretKeyRef)} name="secretKey" type="password" autocomplete="off" ?required=${!this.hasStoredCredential} ?error=${errors.secretKey !== null} label=${this.t("admin.integrations.revolut.wizard.secretKeyLabel", { env: envLabel })} placeholder="sk_…" .value=${this.secretKey} hint=${errors.secretKey ?? ""} @input=${(
						e: CustomEvent<{ value: string }>,
					) => {
						this.secretKey = e.detail.value;
						this.attempted = false;
					}}></schmancy-input>`
		}
    ${
			this.hasStoredPublicKey && !this.replacingPublic
				? html`<schmancy-surface type="containerLow" class="rounded-xl p-3 flex items-center justify-between gap-3"><schmancy-typography type="body" token="md">${this.t("admin.integrations.revolut.wizard.publicKeyLabel", { env: envLabel })}</schmancy-typography><schmancy-button variant="text" @click=${() => {
						this.replacingPublic = true;
					}}>${this.t("admin.integrations.metaAds.form.wizard.replace")}</schmancy-button></schmancy-surface>`
				: html`<schmancy-input ${ref(this.publicKeyRef)} name="publicKey" autocomplete="off" label=${this.t("admin.integrations.revolut.wizard.publicKeyLabel", { env: envLabel })} placeholder="pk_…" .value=${this.publicKey} hint=${errors.publicKey ?? ""} @input=${(
						e: CustomEvent<{ value: string }>,
					) => {
						this.publicKey = e.detail.value;
						this.attempted = false;
					}}></schmancy-input>`
		}
    <schmancy-input ${ref(this.currencyRef)} name="currency" label=${this.t("admin.integrations.revolut.wizard.currencyLabel")} placeholder="EUR" .value=${this.currency} hint=${errors.currency ?? ""} @input=${(
			e: CustomEvent<{ value: string }>,
		) => {
			this.currency = e.detail.value;
			this.attempted = false;
		}}></schmancy-input>
    ${
			this.envId === "revolutLive"
				? html`<schmancy-input ${ref(this.applePayDomainRef)} name="applePayDomain" label=${this.t("admin.integrations.revolut.wizard.applePayDomainLabel")} .value=${this.applePayDomain ?? bareHost(this.storedApplePayDomain ?? workspaceDomain.get())} hint=${errors.applePayDomain ?? ""} @input=${(
						e: CustomEvent<{ value: string }>,
					) => {
						this.applePayDomain = e.detail.value;
						this.attempted = false;
					}}></schmancy-input>`
				: nothing
		}
    ${!this.hasStoredCredential ? html`<schmancy-details summary=${this.t("admin.integrations.revolut.wizard.docsLink")}><schmancy-typography type="body" token="sm">${this.t("admin.integrations.revolut.wizard.prereqNote")}</schmancy-typography><schmancy-button variant="text" href=${REVOLUT_DOCS_URL} target="_blank" rel="noopener">${this.t("admin.integrations.revolut.wizard.docsLink")}<schmancy-icon slot="suffix">open_in_new</schmancy-icon></schmancy-button></schmancy-details>` : nothing}
   </main>
   <schmancy-surface part="footer" opacity="translucent" elevated class="flex shrink-0 flex-wrap items-center justify-between gap-3 px-5 py-4">
    ${
			verifySandbox
				? html`<schmancy-button data-overlay-close variant="text" ?disabled=${busy} @click=${() => this.close()}>${this.t("admin.common.cancel")}</schmancy-button><schmancy-button variant="filled" .busy=${busy} ?disabled=${!canSubmit || busy || (!this.hasStoredPublicKey && !this.publicKey.trim())} @click=${() => {
						this.activate = true;
						void this.save(this.secretKey.trim(), envLabel);
					}}>${this.t("admin.integrations.revolut.confirmation.title")}</schmancy-button>`
				: html`
    <schmancy-button variant="outlined" .busy=${busy && !this.activate} ?disabled=${!canSubmit || busy} @click=${() => {
			this.activate = false;
			void this.save(this.secretKey.trim(), envLabel);
		}}>${this.t("admin.integrations.revolut.wizard.saveButton")}</schmancy-button>
    ${
			!this.active
				? html`<schmancy-button variant="filled" .busy=${busy && this.activate} ?disabled=${!canSubmit || busy || (!this.hasStoredPublicKey && !this.publicKey.trim())} @click=${() => {
						this.activate = true;
						void this.save(this.secretKey.trim(), envLabel);
					}}>${this.t("admin.integrations.revolut.wizard.activateLabel", { env: envLabel })}</schmancy-button>`
				: nothing
		}
    <slot name="navigation"></slot>`
		}

   </schmancy-surface>
  </div>`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"revolut-wizard": RevolutWizardView;
	}
}
